import { useEffect, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Copy, CheckCircle2, UploadCloud, ImageIcon } from "lucide-react";
import { shopApi, STATUS_META, productLabel } from "@/lib/shopApi";
import { money, formatApiErrorDetail } from "@/lib/api";
import { SummaryRow } from "@/pages/store/Cart";

export function OrderStatus({ status }) {
  const m = STATUS_META[status] || { label: status, cls: "bg-slate-50 text-slate-700 border-slate-200" };
  return <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${m.cls}`} data-testid="order-status-badge">{m.label}</span>;
}

function BankCard({ info, order }) {
  const copy = (t) => { navigator.clipboard?.writeText(t); toast.success("Copied"); };
  const Row = ({ k, v, testid }) => (
    <div className="flex items-center justify-between gap-3 py-2"><span className="text-sm text-slate-500">{k}</span>
      <button type="button" onClick={() => copy(v)} className="flex items-center gap-2 font-mono font-semibold text-slate-900 hover:text-sky-800" data-testid={testid}>{v}<Copy className="h-3.5 w-3.5 text-slate-400" /></button></div>
  );
  return (
    <section className="rounded-xl border border-sky-200 bg-white p-5 sm:p-6" data-testid="order-bank-details">
      <h2 className="font-head text-lg font-bold">Pay by bank transfer</h2>
      <div className="mt-3 divide-y divide-slate-100">
        <Row k="Account name" v={info.account_name} testid="bank-account-name" />
        <Row k="BSB" v={info.bsb} testid="bank-bsb" />
        <Row k="Account number" v={info.account_number} testid="bank-account-number" />
        {info.bank_name && <Row k="Bank" v={info.bank_name} testid="bank-name" />}
        <Row k="Amount" v={money(order.total)} testid="bank-amount" />
      </div>
      <div className="mt-4 rounded-lg bg-[#89CFF0]/25 p-4">
        <div className="text-xs font-semibold uppercase tracking-wider text-sky-900">Payment reference</div>
        <button type="button" onClick={() => copy(order.order_number)} className="mt-1 flex items-center gap-2 font-mono text-2xl font-extrabold text-slate-900" data-testid="bank-reference">{order.order_number}<Copy className="h-4 w-4 text-slate-500" /></button>
        <p className="mt-1 text-xs text-slate-600">Use only your order number as the reference.</p>
        <p className="mt-1 text-sm font-extrabold uppercase tracking-wide text-rose-700" data-testid="bank-no-product-names">NO PRODUCT NAMES</p>
      </div>
      {info.bank_instructions && <p className="mt-3 text-sm text-slate-600">{info.bank_instructions}</p>}
    </section>
  );
}

function ProofUpload({ order, onDone }) {
  const input = useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  useEffect(() => {
    if (!order.has_payment_proof) return;
    let url;
    shopApi.get(`/shop/orders/${order.id}/payment-proof`, { responseType: "blob" }).then((r) => { url = URL.createObjectURL(r.data); setPreview(url); }).catch(() => {});
    return () => url && URL.revokeObjectURL(url);
  }, [order.id, order.has_payment_proof]);
  const upload = async (file) => {
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) return toast.error("Image must be 10MB or smaller");
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { await shopApi.post(`/shop/orders/${order.id}/payment-proof`, fd); toast.success("Payment screenshot uploaded"); onDone(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); if (input.current) input.current.value = ""; }
  };
  return (
    <section className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="payment-proof-section">
      <h2 className="font-head text-lg font-bold">Payment screenshot</h2>
      <p className="mt-1 text-sm text-slate-600">After paying, upload a screenshot of your transfer so we can match it faster.</p>
      {preview && <img src={preview} alt="Your payment screenshot" className="mt-4 max-h-64 rounded-lg border border-slate-200" data-testid="payment-proof-preview" />}
      <input ref={input} type="file" accept="image/png,image/jpeg,image/webp,image/heic" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="payment-screenshot-upload-input" />
      <button type="button" disabled={busy} onClick={() => input.current?.click()} data-testid="payment-screenshot-upload-btn"
        className="mt-4 flex w-full flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-sky-200 bg-sky-50/50 p-6 text-sm font-medium text-slate-700 transition-colors hover:border-sky-400 disabled:opacity-60">
        {order.has_payment_proof ? <ImageIcon className="h-6 w-6 text-sky-600" /> : <UploadCloud className="h-6 w-6 text-sky-600" />}
        {busy ? "Uploading…" : order.has_payment_proof ? "Replace screenshot" : "Upload screenshot (JPG, PNG, WEBP)"}
      </button>
    </section>
  );
}

export default function OrderDetail() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [o, setO] = useState(null);
  const [info, setInfo] = useState(null);
  const [err, setErr] = useState(false);
  const load = () => shopApi.get(`/shop/orders/${id}`).then((r) => setO(r.data)).catch(() => setErr(true));
  useEffect(() => { load(); shopApi.get("/shop/store-info").then((r) => setInfo(r.data)).catch(() => {}); }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  if (err) return <div className="py-20 text-center text-slate-500" data-testid="order-not-found">Order not found. <Link to="/account" className="font-semibold text-sky-700">Back to account</Link></div>;
  if (!o || !info) return <div className="py-20 text-center text-slate-500">Loading…</div>;
  const awaiting = ["pending"].includes(o.status);

  return (
    <div data-testid="order-detail-page">
      <Link to="/account" className="inline-flex items-center gap-1.5 text-sm font-medium text-slate-600 hover:text-slate-900" data-testid="order-back-link"><ArrowLeft className="h-4 w-4" /> My orders</Link>
      {params.get("new") && (
        <div className="mt-5 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-4 animate-[fadeUp_.4s_ease-out]" data-testid="order-placed-banner">
          <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-700" />
          <div className="text-sm text-emerald-900"><strong>Order placed.</strong> Please transfer the total using your order number as the reference, then upload your payment screenshot below.</div>
        </div>
      )}
      <div className="mt-6 flex flex-wrap items-center gap-3">
        <h1 className="font-head text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="order-number">{o.order_number}</h1>
        <OrderStatus status={o.status} />
      </div>
      <p className="mt-1 text-sm text-slate-500">Placed {new Date(o.created_at).toLocaleString("en-AU")}</p>

      <div className="mt-8 grid gap-6 lg:grid-cols-[1fr_380px]">
        <div className="space-y-6">
          {awaiting && <BankCard info={info} order={o} />}
          {o.status !== "cancelled" && <ProofUpload order={o} onDone={load} />}
          <section className="rounded-xl border border-slate-200 bg-white p-5 sm:p-6" data-testid="order-delivery">
            <h2 className="font-head text-lg font-bold">{o.delivery_method === "pickup" ? "Local pickup" : "Shipping to"}</h2>
            <div className="mt-2 text-sm leading-relaxed text-slate-600">{o.customer.name}<br />{o.customer.phone}<br />{o.delivery_method !== "pickup" && o.customer.address}</div>
          </section>
        </div>
        <aside className="h-fit space-y-3 rounded-xl border border-slate-200 bg-white p-5" data-testid="order-summary">
          <h2 className="font-head text-lg font-bold">Items</h2>
          {o.items.map((i) => <div key={i.product_id} className="flex justify-between gap-3 text-sm" data-testid={`order-item-${i.product_id}`}><span className="text-slate-700">{productLabel(i)} × {i.qty}</span><span className="font-medium">{money(i.price * i.qty)}</span></div>)}
          <div className="space-y-2 border-t border-slate-100 pt-3">
            <SummaryRow label="Subtotal" value={money(o.subtotal)} />
            {o.discount_amount - (o.shipping_discount || 0) > 0.001 && <SummaryRow label={`Discount (${o.discount_code})`} value={`-${money(o.discount_amount - (o.shipping_discount || 0))}`} accent />}
            {o.auto_discount_amount > 0 && <SummaryRow label={`Bank transfer discount (${o.auto_discount_percent}%)`} value={`-${money(o.auto_discount_amount)}`} accent />}
            <SummaryRow label="Shipping (flat rate)" value={money(o.shipping_cost)} testid="order-shipping" />
            {o.shipping_discount > 0 && <SummaryRow label={`Shipping discount (${o.discount_code})`} value={`-${money(o.shipping_discount)}`} accent />}
            <SummaryRow label="Total (AUD)" value={money(o.total)} strong testid="order-total" />
          </div>
          <div className="border-t border-slate-100 pt-3">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">Status history</div>
            {(o.status_history || []).map((h, idx) => <div key={idx} className="mt-2 flex justify-between text-xs text-slate-600"><span>{STATUS_META[h.status]?.label || h.status}</span><span>{new Date(h.at).toLocaleDateString("en-AU")}</span></div>)}
          </div>
        </aside>
      </div>
    </div>
  );
}
